import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Modal, DatePicker, Select, Button, notification } from "antd";
import { LuX } from "react-icons/lu";
import { CheckIcon, Play, Pause } from "lucide-react";
import moment from "moment";
import {
  useGetVideoResponsesQuery,
  useGetOrganizationsQuery,
  useGetFormsQuery,
  useGetCampaignsQuery,
} from "../../store/api/apiSlice";
import { pickMediaUrl } from "../../utils/formTabs";
import { InterpreterIcon, OrganizationsIcon } from "@/assets/svgs/icons";
import { STATUS_OPTIONS } from "@/pages/admin/Interpreter/constants";
import {
  formatVideoDuration,
  getVideoUrlFromResponse,
  withVideoCacheBuster,
  matchesVideoFilters,
  sortVideoResponses,
} from "../../utils/assignmentsHelpers.js";
import { useInterpreterAssignment } from "../../hooks/useInterpreterAssignment";
import {
  getApiErrorMessage,
  resolveInterpreterId,
} from "../../utils/interpreterAssignment";

const AssignmentVideoCard = ({
  response,
  isSelected,
  isCurrentlyPlaying,
  isPlaying,
  onToggleSelect,
  onPlayVideo,
  setVideoRef,
  onVideoEnded,
}) => {
  const [videoDuration, setVideoDuration] = useState("00:00");
  const localVideoRef = useRef(null);
  const videoSrc = withVideoCacheBuster(
    getVideoUrlFromResponse(response),
    response._id,
  );

  useEffect(() => {
    if (!videoSrc) {
      setVideoDuration("00:00");
      return undefined;
    }

    setVideoDuration("00:00");

    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = videoSrc;

    const updateDuration = () => {
      setVideoDuration(formatVideoDuration(video.duration));
    };

    video.onloadedmetadata = updateDuration;
    video.onerror = () => setVideoDuration("00:00");
    video.load();

    const timeoutId = window.setTimeout(() => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        updateDuration();
      }
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = "";
    };
  }, [videoSrc]);

  const handleLoadedMetadata = (event) => {
    setVideoDuration(formatVideoDuration(event.currentTarget.duration));
  };

  const handleTimeUpdate = (event) => {
    if (!isCurrentlyPlaying || !isPlaying) return;

    const { duration, currentTime } = event.currentTarget;
    if (!Number.isFinite(duration) || duration <= 0) return;

    setVideoDuration(formatVideoDuration(Math.max(0, duration - currentTime)));
  };

  const handleVideoEnded = () => {
    const video = localVideoRef.current;
    if (video && Number.isFinite(video.duration)) {
      setVideoDuration(formatVideoDuration(video.duration));
    } else {
      setVideoDuration("00:00");
    }
    onVideoEnded(response._id);
  };

  useEffect(() => {
    if (isCurrentlyPlaying && isPlaying) return;

    const video = localVideoRef.current;
    if (video && Number.isFinite(video.duration) && video.duration > 0) {
      setVideoDuration(formatVideoDuration(video.duration));
    }
  }, [isCurrentlyPlaying, isPlaying]);

  const organization = response?.form?.organization;
  const organizationLogo = pickMediaUrl(organization?.logo);

  return (
    <div
      className="relative cursor-pointer absolute inset-0 z-10 rounded-xl transition-all duration-200 border border-gray-200"
      onClick={() => onToggleSelect(response._id)}
    >
      <div className="relative aspect-video overflow-hidden rounded-xl">
        {videoSrc ? (
          <video
            ref={(el) => {
              localVideoRef.current = el;
              setVideoRef(response._id, el);
            }}
            src={videoSrc}
            className="h-full w-full object-cover"
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            onDurationChange={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-200 text-sm text-gray-500">
            No video available
          </div>
        )}
        <div
          className={`pointer-events-none absolute inset-0 z-10 rounded-xl border border-gray-200 transition-all duration-200 ${
            isSelected ? "border-2 border-blue-500 bg-blue-500/10" : ""
          }`}
        >
          {isSelected && (
            <div className="absolute right-2 top-2 rounded-full bg-blue-500 p-1">
              <CheckIcon className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
        {videoSrc && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/30">
            <button
              type="button"
              className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border-0 bg-black/40 transition-colors hover:bg-black/55"
              onClick={(event) => onPlayVideo(response._id, event)}
              aria-label={
                isCurrentlyPlaying && isPlaying ? "Pause video" : "Play video"
              }
            >
              {isCurrentlyPlaying && isPlaying ? (
                <Pause className="h-7 w-7 text-white" />
              ) : (
                <Play className="h-7 w-7 text-white" />
              )}
            </button>
          </div>
        )}
        <div className="absolute bottom-2 right-2 z-30 rounded bg-black/50 px-2 py-0.5 text-[10px] text-white">
          {videoDuration}
        </div>
        <div className="absolute left-2 top-2 z-30 rounded bg-black/50 px-2 py-0.5 text-[10px] text-white">
          {response.date_created
            ? moment(response.date_created).format("MMM DD, YYYY")
            : "N/A"}
        </div>
      </div>
      <div className="flex items-center gap-2 p-3">
        <div className="flex-1">
          <h3 className="line-clamp-1 text-sm font-medium">
            {response.question?.title || "Untitled Video"}
          </h3>
          <div className="mt-2 flex items-center gap-2">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
              {!organizationLogo && <OrganizationsIcon />}
              {organizationLogo ? (
                <img
                  src={organizationLogo}
                  alt={organization?.name || "Organisation"}
                  className="h-full w-full object-contain p-1 drop-shadow-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </div>
            <span className="line-clamp-1 text-sm text-gray-500">
              {organization?.name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SelectAssignmentModal = ({ visible, onClose, interpreter, onSubmit }) => {
  const { assignBulkResponses, isAssigning } = useInterpreterAssignment();
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: null,
    organization: null,
    campaign: null,
    form: null,
    sort: null,
    status: undefined,
  });
  const [playingVideo, setPlayingVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const videoRefs = useRef({});
  const videoGridScrollRef = useRef(null);

  const scrollVideoGridToTop = useCallback(() => {
    const scrollContainer = videoGridScrollRef.current;
    if (!scrollContainer) return;

    scrollContainer.scrollTop = 0;
    scrollContainer.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const { data: responseData, isLoading } = useGetVideoResponsesQuery(
    {},
    { skip: !visible },
  );

  const { data: orgsData } = useGetOrganizationsQuery();
  const { data: formsData } = useGetFormsQuery({});
  const { data: campaignsData } = useGetCampaignsQuery();

  useEffect(() => {
    if (!visible) {
      setSelectedAssignments([]);
      setFilters({
        dateRange: null,
        organization: null,
        campaign: null,
        form: null,
        sort: null,
        status: undefined,
      });
      handleStopAllVideos();
    }
  }, [visible]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [interpreter?.interpreter?.avatar]);

  const filterLookups = useMemo(() => {
    const organization = orgsData?.data?.find(
      (org) => org._id === filters.organization,
    );
    const campaign = campaignsData?.data?.find(
      (item) => item._id === filters.campaign,
    );
    const form = formsData?.data?.find((item) => item._id === filters.form);

    return {
      organizationName: organization?.name,
      campaignTitle: campaign?.title,
      formTitle: form?.title,
    };
  }, [
    filters.organization,
    filters.campaign,
    filters.form,
    orgsData,
    campaignsData,
    formsData,
  ]);

  const excludedId = "675174d6b21392b3176d166a";

  const responses = useMemo(() => {
    const list = Array.isArray(responseData?.data) ? responseData.data : [];

    const filtered = list
      .filter((record) => record._id !== excludedId)
      .filter((record) => matchesVideoFilters(record, filters, filterLookups));

    return sortVideoResponses(filtered, filters.sort);
  }, [responseData, filters, filterLookups]);

  useLayoutEffect(() => {
    if (!visible) return;
    scrollVideoGridToTop();
  }, [visible, filters, responses, isLoading, scrollVideoGridToTop]);

  useEffect(() => {
    setSelectedAssignments((prev) =>
      prev.filter((id) => responses.some((record) => record._id === id)),
    );
  }, [responses]);

  const handleStopAllVideos = () => {
    Object.values(videoRefs.current).forEach((videoRef) => {
      if (videoRef) {
        videoRef.pause();
        videoRef.currentTime = 0;
      }
    });
    setPlayingVideo(null);
    setIsPlaying(false);
  };

  const handlePlayVideo = (videoId, event) => {
    event?.stopPropagation?.();

    const videoRef = videoRefs.current[videoId];
    if (!videoRef) return;

    if (playingVideo === videoId) {
      if (videoRef.paused) {
        videoRef
          .play()
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.error("Error playing video:", error);
            setIsPlaying(false);
          });
      } else {
        videoRef.pause();
        setIsPlaying(false);
      }
      return;
    }

    if (playingVideo && videoRefs.current[playingVideo]) {
      videoRefs.current[playingVideo].pause();
      videoRefs.current[playingVideo].currentTime = 0;
    }

    videoRef
      .play()
      .then(() => {
        setPlayingVideo(videoId);
        setIsPlaying(true);
      })
      .catch((error) => {
        console.error("Error playing video:", error);
        setIsPlaying(false);
      });
  };

  const handleVideoEnded = (videoId) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
      setIsPlaying(false);
    }
  };

  const setVideoRef = (videoId, element) => {
    videoRefs.current[videoId] = element;
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedAssignments((prev) =>
      prev.includes(videoId)
        ? prev.filter((id) => id !== videoId)
        : [...prev, videoId],
    );
  };

  const handleSubmit = async () => {
    const interpreterId = resolveInterpreterId(interpreter);
    
    if (!interpreterId) {
      notification.error({
        message: "Cannot assign videos",
        description:
          "Interpreter _id is missing. Refresh the interpreter list and try again.",
      });
    
      return;
    }

    const selectedResponses = responses.filter((response) =>
      selectedAssignments.includes(response._id),
    );

    if (!selectedResponses.length) {
      notification.error({
        message: "Cannot assign videos",
        description: "Select at least one video.",
      });
     
      return;
    }

    try {
      await assignBulkResponses({
        responses: selectedResponses,
        interpreterId,
        interpreter,
        videoRefs,
      });

      notification.success({
        message: "Videos assigned",
        description: `${selectedResponses.length} video${
          selectedResponses.length === 1 ? "" : "s"
        } assigned to the interpreter.`,
      });

      setSelectedAssignments([]);
      onSubmit?.(selectedAssignments);
      onClose();
    } catch (error) {
      notification.error({
        message: "Failed to assign videos",
        description: getApiErrorMessage(
          error,
          "The assignment request was rejected. Check the interpreter and video details and try again.",
        ),
      });
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      closeIcon={
        <div className="bg-secondary flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-gray-200">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={
        <div className="flex flex-wrap items-center gap-3 pr-8">
          <span className="text-xl font-medium">
            Select Assignment ({selectedAssignments.length})
          </span>
        </div>
      }
      className="select-assignment-modal [&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-close]:!rounded-full [&_.ant-modal-close]:overflow-hidden [&_.ant-modal-close:hover]:!rounded-full [&_.ant-modal-close:hover]:!bg-transparent"
      styles={{ body: { overflow: "hidden" } }}
    >
      <div className="flex h-[calc(100vh-140px)] max-h-[calc(100vh-140px)] flex-col">
        <div className="flex shrink-0 justify-between">
          {interpreter && (
            <div className="flex items-center h-12 gap-2 rounded-full border px-1 mb-6 flex shrink-0 justify-start">
              <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
                {interpreter.interpreter?.avatar && !avatarFailed ? (
                  <img
                    src={interpreter.interpreter.avatar}
                    alt={interpreter.interpreter?.name}
                    className="h-8 w-8 object-contain p-0 drop-shadow-sm"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <InterpreterIcon />
                )}
              </div>
              <div className="text-sm px-1">
                <div className="text-gray-900">
                  {interpreter.interpreter?.name}
                </div>
                <div className="text-xs text-gray-500">
                  {interpreter.interpreter?.email}
                </div>
              </div>
            </div>
          )}
          <div className="mb-6 flex shrink-0 justify-end">
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isAssigning}
              disabled={selectedAssignments.length === 0 || isAssigning}
              className="flex h-10 items-center gap-2 rounded-full bg-[#18181B] px-4"
            >
              <CheckIcon size={16} />
              Submit Assignment ({selectedAssignments.length})
            </Button>
          </div>
        </div>
        <div className="mb-4 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-3">
          <DatePicker
            placeholder="Select a date"
            className="h-12 w-full border-gray-200"
            onChange={(date) =>
              setFilters((prev) => ({
                ...prev,
                dateRange: date ? [date, date] : null,
              }))
            }
          />
          <Select
            placeholder="Organisation"
            className="h-12 w-full rounded-full"
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                organization: value || null,
              }))
            }
            value={filters.organization ?? undefined}
          >
            <Select.Option value="">All Organizations</Select.Option>
            {orgsData?.data?.map((org) => (
              <Select.Option key={org._id} value={org._id}>
                {org.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Campaign"
            className="h-12 w-full rounded-full"
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, campaign: value || null }))
            }
            value={filters.campaign ?? undefined}
          >
            <Select.Option value="">All Campaigns</Select.Option>
            {campaignsData?.data?.map((campaign) => (
              <Select.Option key={campaign._id} value={campaign._id}>
                {campaign.title}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Form"
            className="h-12 w-full rounded-full"
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, form: value || null }))
            }
            value={filters.form ?? undefined}
          >
            <Select.Option value="">All Forms</Select.Option>
            {formsData?.data?.map((form) => (
              <Select.Option key={form._id} value={form._id}>
                {form.title}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Status"
            className="h-12 w-full rounded-full"
            value={filters.status}
            allowClear
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, status: value }))
            }
            options={STATUS_OPTIONS}
          />
          <Select
            placeholder="Sort by"
            className="h-12 w-full rounded-full"
            value={filters.sort ?? undefined}
            allowClear
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, sort: value || null }))
            }
          >
            <Select.Option value="asc">Oldest First</Select.Option>
            <Select.Option value="desc">Newest First</Select.Option>
          </Select>
        </div>

        {/* Video Grid */}
        <div
          ref={videoGridScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
        >
          {isLoading ? (
            <div className="py-10 text-center text-gray-500">
              Loading videos...
            </div>
          ) : responses.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {responses.map((response) => (
                <AssignmentVideoCard
                  key={response._id}
                  response={response}
                  isSelected={selectedAssignments.includes(response._id)}
                  isCurrentlyPlaying={playingVideo === response._id}
                  isPlaying={isPlaying}
                  onToggleSelect={toggleVideoSelection}
                  onPlayVideo={handlePlayVideo}
                  setVideoRef={setVideoRef}
                  onVideoEnded={handleVideoEnded}
                />
              ))}
            </div>
          ) : (
            <div className="py-10 text-center text-gray-500">
              No videos match your filter criteria.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SelectAssignmentModal;
